import React, { useState, useEffect } from 'react';
import { Modal, Button, Typography, Alert, message, Spin, Tabs } from 'antd';
import { InboxOutlined, CameraOutlined, FileExcelOutlined } from '@ant-design/icons';
import axios from 'axios';
import { compressImageFile } from '../../utils/compressImage';
import FileUploadTab from './FileUploadTab';
import ExcelUploadTab from './ExcelUploadTab';
import CameraCaptureTab from './CameraCaptureTab';
import ExtractedDataDisplay from './ExtractedDataDisplay';
import AnalysisResults from './AnalysisResults';
import ActionButtons from './ActionButtons';
import { useCamera } from './useCamera';

const { Text } = Typography;
const MOBILE_BREAKPOINT = 768;

const BillUpload = ({ onDataExtracted, onClose }) => {
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [supplierAdded, setSupplierAdded] = useState(false);
  const [productsAdded, setProductsAdded] = useState(false);
  const [addingSupplier, setAddingSupplier] = useState(false);
  const [addingProducts, setAddingProducts] = useState(false);
  const [error, setError] = useState(null);
  
  // UI state
  const [activeTab, setActiveTab] = useState('upload');
  const [editableData, setEditableData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [analysisComplete, setAnalysisComplete] = useState(false);
  const [excelFileList, setExcelFileList] = useState([]);

  // Camera hook
  const {
    isCapturing,
    isStartingCamera,
    capturedImages,
    stream,
    cameraError,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    captureImage,
    removeCapturedImage,
    previewCapturedImage
  } = useCamera();

  // Mobile detection for responsive modal
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const isCameraFullScreen = isMobile && activeTab === 'camera' && isCapturing;

  // File handling functions
  const handleFileChange = (file) => {
    const isValidType = file.type === 'application/pdf' || file.type.startsWith('image/');
    if (!isValidType) {
      message.error('Please upload PDF or image files only.');
      return;
    }
    
    const isLt25M = file.size / 1024 / 1024 < 25;
    if (!isLt25M) {
      message.error('File must be smaller than 25MB.');
      return;
    }
    
    setFileList(prev => [...prev, file]);
  };

  const handleFileRemove = (file) => {
    setFileList(prev => prev.filter(item => item.uid !== file.uid));
  };

  const handleFilePreview = async (file) => {
    if (file.type === 'application/pdf') {
      message.info('PDF preview is not available. The file will be processed when you analyze the bill.');
      return;
    }
    
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj || file);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleExcelFileChange = (file) => {
    const uid = file.uid || `excel-${Date.now()}`;
    setExcelFileList([{ uid, name: file.name, status: 'done', originFileObj: file }]);
  };

  const handleExcelFileRemove = () => {
    setExcelFileList([]);
  };

  const handleImportExcel = async () => {
    const raw = excelFileList[0]?.originFileObj || excelFileList[0];
    if (!raw) {
      Modal.warning({
        title: 'No file selected',
        content: 'Please choose an Excel file (.xlsx or .xls) to import.',
      });
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', raw);

      const response = await axios.post('/purchase-invoice/import-excel', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;
      setExtractedData(data);
      setEditableData({
        supplier: data.supplier
          ? { ...data.supplier }
          : { name: '', phone: '', address: '' },
        billDate: data.billDate,
        billNumber: data.billNumber,
        products: [...(data.products || [])],
      });
      message.success(
        `Imported ${(data.products || []).length} line(s) from Excel (no AI). Review and match suppliers/products below.`
      );
    } catch (error) {
      console.error('Error importing Excel:', error);
      setError(error.response?.data?.message || 'Failed to import Excel. Please check the format and try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyzeBill = async () => {
    const rawFiles = activeTab === 'upload' ? fileList : capturedImages.map(img => img.file);
    
    if (rawFiles.length === 0) {
      Modal.warning({
        title: 'No files selected',
        content: activeTab === 'upload' 
          ? 'Please upload at least one file to analyze.'
          : 'Please capture at least one image to analyze.',
      });
      return;
    }

    setAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      for (let i = 0; i < rawFiles.length; i++) {
        let file = rawFiles[i].originFileObj || rawFiles[i];
        if (file.type?.startsWith('image/') && file.size > 2 * 1024 * 1024) {
          file = await compressImageFile(file);
        }
        formData.append('files', file);
      }

      const response = await axios.post('/purchase-invoice/analyze-bill', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setExtractedData(response.data);
      setEditableData({
        supplier: response.data.supplier
          ? { ...response.data.supplier }
          : { name: '', phone: '', address: '' },
        billDate: response.data.billDate,
        billNumber: response.data.billNumber,
        products: [...(response.data.products || [])]
      });
    } catch (error) {
      console.error('Error analyzing bill:', error);
      setError(error.response?.data?.message || 'Failed to analyze bill. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAddSupplier = async () => {
    if (!extractedData?.newSuppliers?.length) return;
    
    setAddingSupplier(true);
    try {
      const supplier = extractedData.newSuppliers[0];
      const response = await axios.post('/supplier/', {
        name: supplier.name,
        phone: supplier.phone || '',
        address: supplier.address || ''
      });
      
      // Update extracted data with the new supplier ID
      setExtractedData(prev => ({
        ...prev,
        existingSupplier: response.data,
        newSuppliers: []
      }));
      setSupplierAdded(true);
      message.success('Supplier added successfully!');
    } catch (error) {
      console.error('Error adding supplier:', error);
      message.error('Failed to add supplier');
    } finally {
      setAddingSupplier(false);
    }
  };

  const handleAddProducts = async () => {
    if (!extractedData?.newProducts?.length) return;
    
    setAddingProducts(true);
    try {
      const publisherMap = new Map();
      const currencyMap = new Map();
      
      // Create publishers first
      if (extractedData.newPublishers?.length) {
        for (const publisher of extractedData.newPublishers) {
          try {
            const response = await axios.post('/book-publisher/', {
              name: publisher.name
            });
            publisherMap.set(publisher.name, response.data.id);
          } catch (error) {
            console.error('Error creating publisher:', error);
          }
        }
      }
      
      // Create currencies
      if (extractedData.newCurrencies?.length) {
        for (const currency of extractedData.newCurrencies) {
          try {
            const response = await axios.post('/productCurrency/', {
              name: currency.name,
              symbol: currency.symbol || currency.name
            });
            currencyMap.set(currency.name, response.data.id);
          } catch (error) {
            console.error('Error creating currency:', error);
          }
        }
      }
      
      // Create products
      const createdProducts = [];
      for (const product of extractedData.newProducts) {
        try {
          let publisherId = product.publisherId;
          if (!publisherId && product.publisher) {
            publisherId = publisherMap.get(product.publisher);
          }
          
          let currencyId = product.currencyId;
          if (!currencyId && product.currency) {
            currencyId = currencyMap.get(product.currency);
          }
          
          // Get publisher ID from database by name match
          let finalPublisherId = publisherId;
          if (!finalPublisherId && product.publisher) {
            // Try to find existing publisher first
            try {
              const existingPublishers = await axios.get('/book-publisher/?query=all');
              const existingPublisher = existingPublishers.data.find(p => 
                p.name.toLowerCase() === product.publisher.toLowerCase()
              );
              if (existingPublisher) {
                finalPublisherId = existingPublisher.id;
              } else {
                // Create new publisher if not found
                const newPublisher = await axios.post('/book-publisher/', {
                  name: product.publisher
                });
                finalPublisherId = newPublisher.data.id;
              }
            } catch (error) {
              console.error('Error handling publisher:', error);
              // If error is "Publisher already exist", try to find it again
              if (error.response?.data?.message === "Publisher already exist.") {
                try {
                  const retryPublishers = await axios.get('/book-publisher/?query=all');
                  const foundPublisher = retryPublishers.data.find(p => 
                    p.name.toLowerCase() === product.publisher.toLowerCase()
                  );
                  if (foundPublisher) {
                    finalPublisherId = foundPublisher.id;
                  }
                } catch (retryError) {
                  console.error('Error retrying publisher lookup:', retryError);
                }
              }
            }
          }

          // Skip product creation if publisher ID is not available
          if (!finalPublisherId) {
            console.error('Cannot create product without publisher ID:', product);
            continue; // Skip this product
          }

          // Create FormData for product creation (multipart/form-data required)
          const formData = new FormData();
          formData.append("isbn", product.isbn);
          formData.append("name", product.title);
          formData.append("author", product.author || '');
          formData.append("book_publisher_id", finalPublisherId);
          formData.append("product_category_id", 7); // Default category
          formData.append("unit_type", 'copy');
          formData.append("unit_measurement", 1);
          formData.append("product_currency_id", 7); // Default currency
          formData.append("purchase_price", 0); // Set to 0 as requested
          formData.append("sale_price", product.price || 0);
          formData.append("quantity", 0);
          formData.append("image", 'img'); // Default image
          formData.append("reorder_quantity", 1);

          const response = await axios.post('/product/', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          });
          
          createdProducts.push({
            ...response.data,
            extractedQuantity: product.quantity,
            extractedPrice: product.price,
            extractedDiscount: product.discount,
            publisherId: finalPublisherId,
            publisherName: product.publisher
          });
        } catch (error) {
          console.error('Error creating product:', error);
        }
      }
      
      // Update extracted data
      setExtractedData(prev => ({
        ...prev,
        existingProducts: [...(prev.existingProducts || []), ...createdProducts],
        newProducts: [],
        newPublishers: [],
        newCurrencies: []
      }));
      setProductsAdded(true);
      message.success(`${createdProducts.length} products added successfully!`);
    } catch (error) {
      console.error('Error adding products:', error);
      message.error('Failed to add products');
    } finally {
      setAddingProducts(false);
    }
  };

  const handleUseExtractedData = () => {
    // Prepare the data for the purchase form
    const purchaseData = {
      existingSupplier: extractedData.existingSupplier,
      supplier: extractedData.supplier, // Keep original supplier data for reference
      billDate: extractedData.billDate,
      billNumber: extractedData.billNumber,
      existingProducts: extractedData.existingProducts || [],
      newProducts: extractedData.newProducts || [],
      newSuppliers: extractedData.newSuppliers || [],
      newPublishers: extractedData.newPublishers || [],
      newCurrencies: extractedData.newCurrencies || []
    };
    
    console.log('=== DEBUGGING DATA FLOW ===');
    console.log('Full extractedData:', extractedData);
    console.log('Passing data to purchase form:', purchaseData);
    console.log('Existing supplier:', purchaseData.existingSupplier);
    console.log('New suppliers:', purchaseData.newSuppliers);
    console.log('Existing products:', purchaseData.existingProducts);
    console.log('New products:', purchaseData.newProducts);
    console.log('=== END DEBUGGING ===');
    
    onDataExtracted(purchaseData);
  };

  const handleRetry = () => {
    setExtractedData(null);
    setEditableData(null);
    setError(null);
    setAnalysisComplete(false);
    setExcelFileList([]);
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setError(null);
  };

  // Functions to handle editing
  const handleEditSupplier = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      supplier: {
        ...prev.supplier,
        [field]: value
      }
    }));
  };

  const handleEditBillInfo = (field, value) => {
    setEditableData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditProduct = (index, field, value) => {
    setEditableData(prev => ({
      ...prev,
      products: prev.products.map((product, i) => 
        i === index ? { ...product, [field]: value } : product
      )
    }));
  };

  const handleAddProduct = () => {
    setEditableData(prev => ({
      ...prev,
      products: [...prev.products, {
        isbn: '',
        title: '',
        author: '',
        publisher: '',
        quantity: 1,
        price: 0,
        currency: '₹',
        discount: 0
      }]
    }));
  };

  const handleRemoveProduct = (index) => {
    setEditableData(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };

  const handleSaveEdits = () => {
    // Update the extracted data with edited values
    setExtractedData(prev => ({
      ...prev,
      supplier: editableData.supplier,
      billDate: editableData.billDate,
      billNumber: editableData.billNumber,
      products: editableData.products
    }));
    setIsEditing(false);
    setAnalysisComplete(false); // Reset analysis state so user can analyze again
    message.success('Changes saved successfully! You can now proceed to analysis again.');
  };

  const handleCancelEdits = () => {
    setEditableData({
      supplier: extractedData.supplier
        ? { ...extractedData.supplier }
        : { name: '', phone: '', address: '' },
      billDate: extractedData.billDate,
      billNumber: extractedData.billNumber,
      products: [...(extractedData.products || [])]
    });
    setIsEditing(false);
    // Don't reset analysis state on cancel - keep previous analysis results
  };

  const handleProceedToAnalysis = async () => {
    // Update extracted data with any edits made
    const updatedData = {
      ...extractedData,
      supplier: editableData.supplier,
      billDate: editableData.billDate,
      billNumber: editableData.billNumber,
      products: editableData.products
    };
    
    setExtractedData(updatedData);
    
    // Now analyze for new suppliers and products
    try {
      const response = await axios.post('/purchase-invoice/analyze-new-items', {
        supplier: updatedData.supplier,
        products: updatedData.products
      });
      
      console.log('Analysis response:', response.data);
      
      // Update with analysis results - merge the new data
      setExtractedData(prev => ({
        ...prev,
        existingSupplier: response.data.existingSupplier,
        newSuppliers: response.data.newSuppliers || [],
        existingProducts: response.data.existingProducts || [],
        newProducts: response.data.newProducts || [],
        newPublishers: response.data.newPublishers || [],
        newCurrencies: response.data.newCurrencies || []
      }));
      
      setAnalysisComplete(true);
      message.success('Analysis complete! Review new items to be added.');
    } catch (error) {
      console.error('Error analyzing new items:', error);
      message.error('Failed to analyze new items. You can still proceed.');
      setAnalysisComplete(true); // Still allow proceeding
    }
  };

  // Camera preview handler
  const handleCameraPreview = (image) => {
    const preview = previewCapturedImage(image);
    setPreviewImage(preview);
    setPreviewVisible(true);
  };

  const showExtractFooter =
    !extractedData &&
    ((activeTab === 'upload' && fileList.length > 0) ||
      (activeTab === 'camera' && capturedImages.length > 0) ||
      (activeTab === 'excel' && excelFileList.length > 0));

  const runPrimaryAction = () => {
    if (activeTab === 'excel') {
      handleImportExcel();
    } else {
      handleAnalyzeBill();
    }
  };

  const primaryButtonLabel =
    activeTab === 'excel'
      ? analyzing
        ? 'Importing…'
        : 'Import from Excel'
      : analyzing
        ? 'Analyzing Bill…'
        : 'Extract Bill Data';

  return (
    <Modal
      title="Import purchase bill — PDF, image, camera, or Excel"
      open={true}
      onCancel={onClose}
      width={isCameraFullScreen ? '100%' : 1000}
      footer={
        showExtractFooter ? (
          <Button
            type="primary"
            block
            size="large"
            loading={analyzing}
            onClick={runPrimaryAction}
            style={{ height: 48 }}
          >
            {primaryButtonLabel}
          </Button>
        ) : null
      }
      destroyOnClose={true}
      maskClosable={false}
      keyboard={false}
      style={
        isCameraFullScreen
          ? { top: 0, paddingBottom: 0, maxWidth: '100%' }
          : { top: 20 }
      }
      bodyStyle={{
        maxHeight: isCameraFullScreen ? '100vh' : '80vh',
        overflowY: 'auto',
        padding: isCameraFullScreen ? '4px 8px' : (isMobile ? '12px' : '16px')
      }}
    >
      <div style={{ padding: isCameraFullScreen ? '0' : '20px 0' }}>
        {!extractedData && (
          <>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              size={isCameraFullScreen ? 'small' : 'default'}
              style={isCameraFullScreen ? { marginTop: -4, marginBottom: 0 } : undefined}
              items={[
                {
                  key: 'upload',
                  label: (
                    <span>
                      <InboxOutlined />
                      Upload Files
                    </span>
                  ),
                  children: (
                    <FileUploadTab
                      fileList={fileList}
                      onFileChange={handleFileChange}
                      onFileRemove={handleFileRemove}
                      onFilePreview={handleFilePreview}
                    />
                  ),
                },
                {
                  key: 'excel',
                  label: (
                    <span>
                      <FileExcelOutlined />
                      Excel
                    </span>
                  ),
                  children: (
                    <ExcelUploadTab
                      fileList={excelFileList}
                      onFileChange={handleExcelFileChange}
                      onFileRemove={handleExcelFileRemove}
                    />
                  ),
                },
                {
                  key: 'camera',
                  label: (
                    <span>
                      <CameraOutlined />
                      Camera Capture
                    </span>
                  ),
                  children: (
                    <CameraCaptureTab
                      isCapturing={isCapturing}
                      isStartingCamera={isStartingCamera}
                      cameraError={cameraError}
                      capturedImages={capturedImages}
                      stream={stream}
                      videoRef={videoRef}
                      canvasRef={canvasRef}
                      onStartCamera={startCamera}
                      onStopCamera={stopCamera}
                      onCaptureImage={captureImage}
                      onRemoveImage={removeCapturedImage}
                      onPreviewImage={handleCameraPreview}
                    />
                  ),
                },
              ]}
            />


            {analyzing && (
              <div style={{ textAlign: 'center', padding: '40px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: 16 }}>
                  <Text>
                    {activeTab === 'excel'
                      ? 'Reading spreadsheet…'
                      : 'AI is analyzing your bill...'}
                  </Text>
                  <br />
                  <Text type="secondary">
                    {activeTab === 'excel'
                      ? 'No AI — parsing rows locally on the server'
                      : 'This may take a few moments'}
                  </Text>
                </div>
              </div>
            )}

            {error && (
              <Alert
                message="Analysis Failed"
                description={error}
                type="error"
                showIcon
                action={
                  <Button size="small" onClick={handleRetry}>
                    Retry
                  </Button>
                }
                style={{ marginTop: 16 }}
              />
            )}
          </>
        )}

        {extractedData && (
          <>
            <ExtractedDataDisplay
              extractedData={extractedData}
              editableData={editableData}
              isEditing={isEditing}
              onEditToggle={() => {
                if (!isEditing) {
                  // When starting to edit, reset analysis state and clear analysis results
                  setAnalysisComplete(false);
                  setExtractedData(prev => ({
                    ...prev,
                    existingSupplier: null,
                    newSuppliers: [],
                    existingProducts: [],
                    newProducts: [],
                    newPublishers: [],
                    newCurrencies: []
                  }));
                }
                setIsEditing(!isEditing);
              }}
              onEditSupplier={handleEditSupplier}
              onEditBillInfo={handleEditBillInfo}
              onEditProduct={handleEditProduct}
              onAddProduct={handleAddProduct}
              onRemoveProduct={handleRemoveProduct}
              onSaveEdits={handleSaveEdits}
              onCancelEdits={handleCancelEdits}
            />

            <AnalysisResults
              extractedData={extractedData}
              analysisComplete={analysisComplete}
            />

            <ActionButtons
              isEditing={isEditing}
              analysisComplete={analysisComplete}
              extractedData={extractedData}
              supplierAdded={supplierAdded}
              productsAdded={productsAdded}
              addingSupplier={addingSupplier}
              addingProducts={addingProducts}
              onProceedToAnalysis={handleProceedToAnalysis}
              onAddSupplier={handleAddSupplier}
              onAddProducts={handleAddProducts}
              onUseExtractedData={handleUseExtractedData}
              onRetry={handleRetry}
            />
          </>
        )}

        <Modal
          open={previewVisible}
          title="Image Preview"
          footer={null}
          onCancel={() => setPreviewVisible(false)}
        >
          <img alt="preview" style={{ width: '100%' }} src={previewImage} />
        </Modal>
      </div>
    </Modal>
  );
};

export default BillUpload;
